import { strings } from '@ch-apptitude-icc/common/shared/utils';
import { testAvsNumber } from '@ch-apptitude-icc/common/shared/validation';
import { Role } from '@ch-apptitude-icc/lablink/shared/entities';
import {
  Button,
  Card,
  SortableHeader,
  SortableHeaderProps,
  Table,
  TableColumn,
  tableColumnHelper,
  TableSort,
} from '@features/ui/components';
import { PatientFront, useApi } from '@services/api';
import { useFormatters, UseFormatters } from '@services/formatters';
import { getRoute } from '@services/routes';
import { useAppSelector } from '@services/store/hooks';
import { selectSearchString } from '@services/store/pageStateSlice';
import { convertGender } from '@utils/convertGender';
import { useRouter } from 'next/router';
import { TFunction, useTranslation } from 'next-i18next';
import { useState } from 'react';

const columnHelper = tableColumnHelper<PatientFront>();

const columns: (
  t: TFunction,
  sort: TableSort | null,
  setSort: SortableHeaderProps['onSort'],
  fDate: UseFormatters['fDate'],
  canCreateOrder: boolean | undefined,
) => TableColumn<PatientFront> = (t, sort, setSort, fDate, canCreateOrder) => [
  columnHelper.accessor({
    accessor: 'lastname',
    column: {
      header: () => (
        <SortableHeader
          label={t('entities.patient.lastName')}
          selector="lastname"
          onSort={setSort}
          currentSort={sort}
        />
      ),
      cell: info => <span className="font-medium uppercase text-gray-800">{info.getValue()}</span>,
    },
    withClassName: 'w-52 max-w-52 overflow-hidden text-ellipsis',
  }),
  columnHelper.accessor({
    accessor: 'firstname',
    column: {
      header: () => (
        <SortableHeader
          label={t('entities.patient.firstName')}
          selector="firstname"
          onSort={setSort}
          currentSort={sort}
        />
      ),
      cell: info => <span className="font-medium text-gray-800">{info.getValue()}</span>,
    },
    withClassName: 'w-52 max-w-52 overflow-hidden text-ellipsis',
  }),
  columnHelper.accessor({
    accessor: 'birthDate',
    column: {
      header: () => (
        <SortableHeader
          label={t('entities.patient.birthDate')}
          selector="birthDate"
          onSort={setSort}
          currentSort={sort}
        />
      ),
      cell: info => (
        <span className="font-medium text-gray-800">
          {/* Birthdate is not correctly typed from backend */}
          {fDate(new Date(info.getValue() as unknown as string))}
        </span>
      ),
    },
    withClassName: 'w-32',
  }),
  columnHelper.accessor({
    accessor: 'gender',
    column: {
      header: () => (
        <SortableHeader
          label={t('entities.patient.gender.gender')}
          selector="gender"
          onSort={setSort}
          currentSort={sort}
        />
      ),
      cell: info => <span className="capitalize">{convertGender(info.getValue(), t)}</span>,
    },
    withClassName: 'w-28 hidden lg:table-cell',
  }),
  columnHelper.accessor({
    accessor: 'address.city',
    column: {
      header: () => (
        <SortableHeader
          label={t('entities.patient.address.city')}
          selector="city"
          onSort={setSort}
          currentSort={sort}
        />
      ),
    },
    withClassName: 'w-40 hidden lg:table-cell',
  }),
  columnHelper.accessor({
    accessor: 'socialSecurityNumber',
    column: {
      header: columnHelper.trAsString(t('entities.patient.avsNo')),
      cell: ({ getValue }) => {
        const value = getValue();
        // Some SSN are literally "incorrect"
        return value && testAvsNumber(value) ? strings.toSsnDisplay(value) : value;
      },
    },
    withClassName: 'w-40 hidden lg:table-cell',
  }),
  // columnHelper.accessor({
  //   accessor: 'references',
  //   column: {
  //     header: columnHelper.trAsString(t('entities.patient.reference')),
  //     cell: () => <span className="uppercase">TODO</span>,
  //   },
  //   withClassName: 'hidden lg:table-cell',
  // }),
  columnHelper.display({
    column: {
      header: '',
      cell: info =>
        canCreateOrder && (
          <div className="flex justify-center">
            <Button
              label={t('pages.patients.table.newOrder') ?? 'Nouvelle commande'}
              leftIcon="inbox"
              stopPropagation
              url={getRoute.orderNew({ query: { patientId: info.row.original.id.toString() } })}
              variant="table"
            />
          </div>
        ),
    },
    withClassName: 'hidden lg:table-cell',
  }),
];

export const PatientsTable = (): JSX.Element => {
  const { t } = useTranslation();
  const { fDate } = useFormatters();
  const router = useRouter();

  const searchString = useAppSelector(selectSearchString);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sort, setSort] = useState<TableSort | null>({ id: 'lastname' satisfies keyof PatientFront, direction: 'ASC' });

  const { patientApi, authApi } = useApi();
  const { data, isLoading, isError } = patientApi.useGetList({
    filtered: searchString ? { searchColumn: { $like: `%${searchString}%` } } : undefined,
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    sorted: sort
      ? {
          [sort.id as keyof PatientFront]: sort.direction,
        }
      : undefined,
  });

  const canCreateOrder = authApi.useHasRole(Role.DOCTOR);

  const translatedColumns = columns(t, sort, setSort, fDate, canCreateOrder.data);

  return (
    <Card header={t('pages.patients.table.title')}>
      <div className="h-152">
        <Table
          columns={translatedColumns}
          data={data}
          isError={isError}
          isLoading={isLoading}
          pagination={[pagination, setPagination]}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onRowClick={row => router.push(getRoute.patientDetail(row.id).pathname)}
          noResultsItems={[
            t('entities.patient.avsNo'),
            t('pages.patients.table.noResultsItems.firstAndLastName'),
            t('pages.patients.table.noResultsItems.birthDate'),
          ]}
        />
      </div>
    </Card>
  );
};
